import styled from '@emotion/styled';
import { CustomTheme } from '../../context/PermissionsContext/ThemeContext/themesTypes';

export const ViewContainer = styled.div<{ theme?: CustomTheme }>`
  padding: 0;
  max-width: 100%;
  margin: 0;
  background-color: ${({ theme }) => theme.custom.background2};
`;

export const HeaderContainer = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: ${({ theme }) => theme.custom.background1};
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderTitle = styled.h1<{ theme?: CustomTheme }>`
  font-family: Manrope;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;
  letter-spacing: -0.2px;
  color: ${({ theme }) => theme.custom.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HeaderStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const StatItem = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.custom.textLight};
  font-size: 14px;
  font-weight: 500;

  img {
    width: 16px;
    height: 16px;
    opacity: 0.6;
  }
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const NewGameButton = styled.button<{ theme?: CustomTheme }>`
  background: ${({ theme }) => theme.custom.primary};
  color: ${({ theme }) => theme.custom.textCTA};
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-family: Space Grotesk;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.custom.primaryDark};
  }
`;

export const SettingsButton = styled.button<{ theme?: CustomTheme }>`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.custom.textLight};

  &:hover {
    color: ${({ theme }) => theme.custom.text};
  }
`;

export const MainContent = styled.div<{ theme?: CustomTheme }>`
  padding: 24px;
  background: ${({ theme }) => theme.custom.background2};
`;

export const TabsContainer = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  background: ${({ theme }) => theme.custom.background1};
  padding: 0;
  margin: 0;
  padding-left: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.custom.stroke};
  position: relative;
`;

export const Tab = styled.div<{ isActive: boolean; theme?: CustomTheme }>`
  font-family: Space Grotesk;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme, isActive }) =>
    isActive ? theme.custom.text : theme.custom.textLight};
  padding: 12px 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  line-height: 20px;

  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.custom.primary : 'transparent'};
    transition: all 0.2s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.custom.text};
  }
`;

export const GameCardsContainer = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 16px;
  margin-top: 16px;
  margin-bottom: 32px;
  flex-wrap: nowrap;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.custom.background3};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.custom.primary};
    border-radius: 3px;
  }
`;

export const GameCard = styled.div<{ theme?: CustomTheme }>`
  display: flex;
  flex-direction: column;
  width: 350px;
  min-width: 350px;
  height: fit-content;
  cursor: pointer;
  background: ${({ theme }) => theme.custom.background1};
  flex-shrink: 0;
  border-radius: 0px 0px 8px 8px;
`;

export const GameCardImage = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const GameCardContent = styled.div<{ theme?: CustomTheme }>`
  padding: 16px;
  background: ${({ theme }) => theme.custom.background1};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

export const GameCardEllipsis = styled.div<{ theme?: CustomTheme }>`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  background: white;
  backdrop-filter: blur(4px);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;

  &:hover {
    background: ${({ theme }) => `${theme.custom.background1}80`};
  }
`;

export const GameCardTitle = styled.h3<{ theme?: CustomTheme }>`
  font-family: Space Grotesk;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.custom.text};
  margin: 0 0 4px 0;
`;

export const GameCardSubtitle = styled.p<{ theme?: CustomTheme }>`
  font-family: Space Grotesk;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.custom.textLight};
  margin: 0;
`;

export const LoadingContainer = styled.div<{ theme?: CustomTheme }>`
  width: 100%;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.custom.background1};
`;

export const ErrorContainer = styled.div<{ theme?: CustomTheme }>`
  width: 100%;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Space Grotesk;
  font-size: 16px;
  color: ${({ theme }) => theme.custom.textLight};
`;

export const GamesDimensionTypeContainer = styled.div`
  padding: 6px;
  display: flex;
  gap: 4px;
  background-color: #f0f0f3;
  border-radius: 10px;
`;

export const GamesDimensionTypeTab = styled.div<{ isActive: boolean }>`
  font-family: Space Grotesk;
  color: ${(props) => (props.isActive ? '#FF6A00' : '#5F5F5F')};
  background-color: ${(props) => (props.isActive ? '#fff' : 'transparent')};
  text-align: center;
  padding: 10px 40px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
`;

export const StatusFilterContainer = styled.div`
  display: flex;
  gap: 20px;
`;

export const StatusFilterTab = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

export const StatusFilterTabText = styled.div<{ isActive: boolean }>`
  color: ${(props) => (props.isActive ? '#FF6A00' : '#5F5F5F')};
  font-weight: 700;
`;

export const StatusFilterTabIndicator = styled.div<{ isActive: boolean }>`
  width: 10px;
  height: 10px;
  background-color: ${(props) => (props.isActive ? '#FF6A00' : 'transparent')};
  border-radius: 100px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
`;

export const PreviousNextButton = styled.div<{ disabled: boolean }>`
  font-size: 38px;
  font-weight: 500;
  color: ${(props) => (props.disabled ? '#f0f0f3' : '#FF6A00')};
  cursor: pointer;
`;

export const GamesAddIconContainer = styled.div`
  position: relative;
  align-self: flex-end;
  margin-bottom: 20px;
`;

export const GamesAddIcon = styled.img`
  width: 30;
  height: 30;
  cursor: pointer;
`;

export const AddModalContainer = styled.div`
  position: absolute;
  min-width: 360px;
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  right: 0;
`;

export const AddModalTitle = styled.div`
  font-family: Space Grotesk;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 16px;
`;

export const AddModalItem = styled.div`
  padding: 10px;
  background-color: #f0f0f3;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  border-radius: 10px;
  cursor: pointer;
  justify-content: space-between;
`;

export const AddModalItemLeftSide = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

export const AddModalItemRightSide = styled.div`
  display: flex;
  align-self: center;
`;

export const AddModalIcon = styled.div`
  padding: 6px;
  background-color: #fff;
  display: flex;
  border-radius: 10px;
`;

export const AddModalItemTitle = styled.div<{ disabled: boolean }>`
  color: ${({ disabled }) => (disabled ? '#CCCCCC' : '#FF6A00')};
  font-family: Space Grotesk;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
`;

export const NoGameContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
  margin-top: 50px;
`;

export const NoGameIcon = styled.img`
  width: 150px;
  height: 150px;
`;

export const NoGameText = styled.div`
  font-size: 20px;
  font-family: Space Grotesk;
  font-weight: 500;
`;

export const CreateNewButton = styled.div`
  background-color: #ff6a00;
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  font-family: Manrope;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  color: #fff;

  @media (min-width: 600px) {
    width: 220px;
  }
`;

export const GameSectionContainer = styled.div`
  margin-bottom: 32px;
`;

export const GameSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const GameSectionTitle = styled.h2<{ theme?: CustomTheme }>`
  font-family: Space Grotesk;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.custom.text};
  margin: 0;
  margin-top: 32px;

  &:first-child {
    margin-top: 0;
  }
`;

export const ViewAllButton = styled.div`
  color: #ff6a00;
  font-family: Space Grotesk;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

export const SkeletonContainer = styled.div<{ theme?: CustomTheme }>`
  width: 100%;
  min-height: 300px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  background: transparent;
`;

export const SkeletonCardsContainer = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: hidden;
  padding-bottom: 16px;
  margin-top: 16px;
  margin-bottom: 32px;
  flex-wrap: nowrap;
`;

export const SkeletonCard = styled.div<{ theme?: CustomTheme }>`
  width: 350px;
  min-width: 350px;
  height: 400px;
  background: transparent;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;

  .image {
    width: 100%;
    height: 300px;
    background: ${({ theme }) => theme.custom.background3};
    border-radius: 8px 8px 0 0;
  }

  .title {
    width: 60%;
    height: 24px;
    margin: 16px;
    background: ${({ theme }) => theme.custom.background3};
    border-radius: 4px;
  }

  .subtitle {
    width: 40%;
    height: 16px;
    margin: 0 16px 16px;
    background: ${({ theme }) => theme.custom.background3};
    border-radius: 4px;
  }
`;

export const EmptyStateMessage = styled.div<{ theme?: CustomTheme }>`
  width: 100%;
  min-height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Space Grotesk;
  font-size: 16px;
  color: ${({ theme }) => theme.custom.textLight};
`;
